import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { changeDeal } from 'bundles/apex/deals/form/store/deal/actions';

import AddressFields from 'bundles/apex/deals/form/fields/address_fields';
import AgreementType from 'bundles/apex/deals/form/fields/agreement_type';
import Area from 'bundles/apex/deals/form/fields/area';
import CalculationMode from 'bundles/apex/deals/form/fields/calculation_mode';
import CriticalDate from 'bundles/apex/deals/form/fields/critical_date';
import CancelButton from 'bundles/apex/deals/form/cancel_button';
import Contacts from 'bundles/apex/deals/form/fields/contacts';
import Currency from 'bundles/apex/deals/form/fields/currency';
import CustomFields from 'bundles/apex/deals/form/fields/custom_fields';
import DealType from 'bundles/apex/deals/form/fields/deal_type';
import Errors from 'bundles/apex/deals/form/errors';
import GrossCommission from 'bundles/apex/deals/form/fields/gross_commission';
import InternalBrokers from 'bundles/apex/deals/form/fields/internal_brokers';
import LeaseStructure from 'bundles/apex/deals/form/fields/lease_structure';
import LeaseRate from 'bundles/apex/deals/form/fields/lease_rate';
import LeaseTerm from 'bundles/apex/deals/form/fields/lease_term';
import LeaseType from 'bundles/apex/deals/form/fields/lease_type';
import ManagedInput from 'components/form/managed_input';
import Numeric from 'bundles/apex/deals/form/fields/numeric';
import OutsideBrokers from 'bundles/apex/deals/form//fields/outside_brokers';
import Percentage from 'bundles/apex/deals/form/fields/percentage';
import Property from 'bundles/apex/deals/form/fields/property';
import PropertyType from 'bundles/apex/deals/form/fields/property_type';
import SaveButton from 'bundles/apex/deals/form/save_button';
import Section from './section';
import SpaceSize from 'bundles/apex/deals/form/fields/space_size';
import Stage from 'bundles/apex/deals/form/fields/stage';
import Text from 'bundles/apex/deals/form/fields/text';
import TextArea from 'bundles/apex/deals/form/fields/textarea';
import TransactionSideSubsection from './transaction_side_subsection';
import TransactionValue from 'bundles/apex/deals/form/fields/transaction_value';
import { BUY_SIDE, SELL_SIDE } from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { getPropertySectionLabel } from 'bundles/apex/deals/helpers/labels';

import './styles.scss';

export function DealForm({ changeDeal, deal, transactionSidesEnabled, onCancel, onSave }) {
  const criticalDates = (
    <Section label="Critical Dates">
      <CriticalDate name="listingExecutionDate" />
      <CriticalDate name="listingExpirationDate" />
      <CriticalDate name="contractExecutionDate" />
      <CriticalDate name="closeDate" />
      <CriticalDate name="leaseStartDate" />
      <CriticalDate name="leaseExpirationDate" />
      <CriticalDate name="renewalOptionNoticeDate" />
      <CustomFields forDates />
    </Section>
  );

  return (
    <div className="deal-form deal-form-full form form-horizontal">
      <Errors/>

      <Section label="Deal">
        <CalculationMode/>
        <DealType/>
        <Stage/>
        <Text name="name" />
        <Text name="identifier" />
        <Text name="deadReason" />
        <Percentage name="closeProbability" />
      </Section>

      { !transactionSidesEnabled &&
        <React.Fragment>
          <Section label="Contacts">
            <InternalBrokers/>
            <OutsideBrokers/>
            <Contacts/>
          </Section>
          {criticalDates}
        </React.Fragment> }

      <Section label={getPropertySectionLabel(deal)}>
        <Property/>
        <PropertyType/>
        <TextArea name="requirements" />
        <Text name="propertyName" />
        <AddressFields/>
        <Numeric name="buildingSize" />
        <Area name="lotSize" />
        <SpaceSize/>
        <Numeric name="numberOfUnits" />
        <Percentage name="occupancyPercent" />
        <Numeric name="yearBuilt" />
        <CustomFields category="property" />
      </Section>

      <Section label="Transaction">
        <AgreementType />
        <LeaseType/>
        <LeaseRate/>
        <LeaseTerm/>
        <TransactionValue/>
        {!deal.hasTransactionSides && <GrossCommission/>}
        <LeaseStructure modalEditing />
        <Numeric name="capRate" />
        <Currency name="netOperatingIncome" />
        <CustomFields category="financials" />
      </Section>

      { transactionSidesEnabled &&
        <React.Fragment>
          {criticalDates}
          <Section label="Brokers">
            { deal.hasTransactionSides
              ? (
                <React.Fragment>
                  <TransactionSideSubsection deal={deal} transactionSide={SELL_SIDE} />
                  <TransactionSideSubsection deal={deal} transactionSide={BUY_SIDE} />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <InternalBrokers/>
                  <OutsideBrokers/>
                </React.Fragment>
              )}
          </Section>

          <Section label="Contacts">
            <Contacts/>
          </Section>
        </React.Fragment> }

      <Section label="Custom Fields">
        <CustomFields category={null} />
      </Section>

      {deal.canActivateVoucher &&
        <div className="text-right">
          <ManagedInput
            label="Create voucher"
            type="checkbox"
            value={deal.activateVoucher}
            onValueChange={activateVoucher => changeDeal({ activateVoucher })}
          />
        </div>}

      <div className="text-right">
        <CancelButton className="mr-2" onCancel={onCancel} />
        <SaveButton onSave={onSave} />
      </div>
    </div>
  );
}

DealForm.propTypes = {
  changeDeal: PropTypes.func.isRequired,
  deal: PropTypes.shape({
    canActivateVoucher: PropTypes.bool,
  }).isRequired,
  transactionSidesEnabled: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  deal: state.deal,
  transactionSidesEnabled: state.features.transactionSideDetails,
});

const mapDispatchToProps = {
  changeDeal,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealForm);
